import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Upload } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useSiteMedia } from "../context/SiteMedia";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const { t } = useLanguage();
  const { refresh: refreshSiteMedia } = useSiteMedia();
  const a = t.admin;
  const [pin, setPin] = useState("");
  const [authedPin, setAuthedPin] = useState(null);
  const [media, setMedia] = useState([]);
  const [file, setFile] = useState(null);
  const [slot, setSlot] = useState("gallery");
  const [alt, setAlt] = useState("");
  const [uploading, setUploading] = useState(false);

  const loadMedia = useCallback(
    async (activePin) => {
      const res = await axios.get(`${API}/media/list`, {
        headers: { "X-Admin-Pin": activePin },
      });
      setMedia(res.data);
    },
    []
  );

  useEffect(() => {
    if (authedPin) loadMedia(authedPin).catch(() => {});
  }, [authedPin, loadMedia]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await loadMedia(pin);
      setAuthedPin(pin);
    } catch {
      toast.error(a.wrongPin);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || uploading) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("slot", slot);
      form.append("alt", alt);
      await axios.post(`${API}/media/upload`, form, {
        headers: { "X-Admin-Pin": authedPin },
      });
      toast.success(a.uploadSuccess);
      setFile(null);
      setAlt("");
      e.target.reset();
      await loadMedia(authedPin);
      refreshSiteMedia();
    } catch {
      toast.error(a.uploadError);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.post(`${API}/media/${id}/delete`, null, {
        headers: { "X-Admin-Pin": authedPin },
      });
      toast.success(a.deleteSuccess);
      await loadMedia(authedPin);
      refreshSiteMedia();
    } catch {
      toast.error(a.uploadError);
    }
  };

  const slotKeys = ["gallery", "logo", "portrait"];

  return (
    <>
      <Seo title="Admin — Pierre Coaching" description="Media beheer" />
      <section className="pt-16 pb-20 md:py-24 min-h-[70vh]" data-testid="admin-page">
        <div className="max-w-3xl mx-auto px-6">
          <h1 className="font-display uppercase text-3xl sm:text-4xl tracking-tight text-white mb-10">
            {a.title}
          </h1>

          {!authedPin ? (
            <form
              onSubmit={handleLogin}
              className="bg-coal-card p-8 rounded-lg border border-line max-w-sm"
              data-testid="admin-login-form"
            >
              <label htmlFor="admin-pin" className="block text-xs font-mono uppercase tracking-wider text-gold-dim mb-2">
                {a.pinLabel}
              </label>
              <input
                id="admin-pin"
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                required
                data-testid="admin-pin-input"
                className="w-full bg-coal-field border border-line-field rounded px-4 py-3 text-sm text-white focus:outline-none focus:border-gold mb-4"
              />
              <button
                type="submit"
                data-testid="admin-login-button"
                className="w-full py-3 bg-gold text-black font-semibold text-sm tracking-wider uppercase rounded hover:bg-gold-bright transition-colors"
              >
                {a.pinButton}
              </button>
            </form>
          ) : (
            <div className="space-y-10">
              <form
                onSubmit={handleUpload}
                className="bg-coal-card p-8 rounded-lg border border-line space-y-5"
                data-testid="admin-upload-form"
              >
                <h2 className="font-display uppercase text-xl text-white">{a.uploadTitle}</h2>

                <div>
                  <label htmlFor="admin-slot" className="block text-xs font-mono uppercase tracking-wider text-gold-dim mb-2">
                    {a.slotLabel}
                  </label>
                  <select
                    id="admin-slot"
                    value={slot}
                    onChange={(e) => setSlot(e.target.value)}
                    data-testid="admin-slot-select"
                    className="w-full bg-coal-field border border-line-field rounded px-4 py-3 text-sm text-white focus:outline-none focus:border-gold"
                  >
                    {slotKeys.map((s) => (
                      <option key={s} value={s}>
                        {a.slots[s]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="admin-file" className="block text-xs font-mono uppercase tracking-wider text-gold-dim mb-2">
                    {a.chooseFile}
                  </label>
                  <input
                    id="admin-file"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    required
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid="admin-file-input"
                    className="w-full text-sm text-bone-soft file:mr-4 file:py-2.5 file:px-4 file:rounded file:border-0 file:text-xs file:font-semibold file:uppercase file:tracking-wider file:bg-gold file:text-black hover:file:bg-gold-bright file:cursor-pointer"
                  />
                </div>

                <div>
                  <label htmlFor="admin-alt" className="block text-xs font-mono uppercase tracking-wider text-gold-dim mb-2">
                    {a.altLabel}
                  </label>
                  <input
                    id="admin-alt"
                    type="text"
                    value={alt}
                    onChange={(e) => setAlt(e.target.value)}
                    maxLength={140}
                    data-testid="admin-alt-input"
                    className="w-full bg-coal-field border border-line-field rounded px-4 py-3 text-sm text-white focus:outline-none focus:border-gold"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!file || uploading}
                  data-testid="admin-upload-button"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-black font-semibold text-sm tracking-wider uppercase rounded hover:bg-gold-bright transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? a.uploading : a.uploadButton}
                </button>
              </form>

              <div data-testid="admin-media-list">
                <h2 className="font-display uppercase text-xl text-white mb-6">{a.listTitle}</h2>
                {media.length === 0 ? (
                  <p className="text-sm text-bone-mute">{a.empty}</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {media.map((m) => (
                      <figure
                        key={m.id}
                        className="relative bg-coal-card border border-line rounded-lg overflow-hidden group"
                        data-testid={`admin-media-item-${m.id}`}
                      >
                        <img
                          src={`${BACKEND_URL}${m.url}`}
                          alt={m.alt || m.original_filename}
                          className="w-full aspect-square object-cover"
                        />
                        <figcaption className="p-2 flex items-center justify-between gap-2">
                          <span className="text-[10px] font-mono uppercase tracking-wider text-gold-dim">
                            {a.slots[m.slot] || m.slot}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleDelete(m.id)}
                            aria-label={a.deleteButton}
                            data-testid={`admin-delete-${m.id}`}
                            className="text-bone-mute hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
